/**
 * Onboarding - Dataverse API Integration
 * Following customer feature pattern
 */

import { ICommonListApiRes, ICommonQueryParams, NetworkMethodEnum } from "@/types";
import { MiddlewareAPI, API } from "@/lib/axios";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { mutator } from "@/utils/api/dataverse/mutator";
import { APPENV } from "@/config/env";
import { generateQueryParamsForPagination } from "@/utils/pagination";
import { IOnboardingAccount, IOnboardingForm, ICreateAccountResponse } from "../types";
import { formatPhoneNumber } from "../utils";

export const URL = {
  accounts: "/AccountApi",
  createAccountWithContact: "/CreateAccountWithContact",
  email: "/EmailApi",
};

/**
 * Create a new onboarding account with contact
 */
export const createOnboardingAccount = async (
  data: IOnboardingForm
): Promise<ICreateAccountResponse> => {
  // Format phone number if provided
  const { Currency, ...rest } = data;
  const payload = {
    ...rest,
    "Primary Contact Phone":
      formatPhoneNumber(data["Primary Contact Phone"]) || "",
    "Trading Name": data["Trading Name"],
    ...(Currency ? { "Dealers Currency": Currency } : {}),
    "Delivery Charge": data["Delivery Charge"],
  };
  return await mutator(NetworkMethodEnum.POST, URL.createAccountWithContact, payload);
};

/**
 * Update onboarding account and contact information
 * Uses CreateAccountWithContact endpoint — maps form fields to Dataverse field names
 */
export const updateOnboardingContact = async (data: {
  accountId: string;
  contactId?: string;
  formValues: Partial<IOnboardingForm>;
}): Promise<ICreateAccountResponse> => {
  const { Currency, ...restF } = data.formValues;
  const payload: Record<string, any> = {
    accountId: data.accountId,
    contactId: data.contactId,
    ...restF,
    ...(Currency ? { "Dealers Currency": Currency } : {}),
    "Delivery Charge": data.formValues["Delivery Charge"],
  };

  return await mutator(
    NetworkMethodEnum.PATCH,
    URL.createAccountWithContact,
    payload,
  );
};

/**
 * Update onboarding account information (non-contact fields)
 * Uses standard AccountApi endpoint
 */
export const updateOnboardingAccount = async (data: {
  id: string;
  payload: Partial<IOnboardingForm>;
}): Promise<IOnboardingAccount> => {
  const updatePayload: any = {
    id: data.id,
  };

  // Only include fields that are present in the payload
  if (data.payload["Company Name"] !== undefined) {
    updatePayload.name = data.payload["Company Name"];
  }
  if (data.payload["Trading Name"] !== undefined) {
    updatePayload.dogma_tradingname = data.payload["Trading Name"];
  }
  if (data.payload["Prefix"] !== undefined) {
    updatePayload.dogma_prefix = data.payload["Prefix"];
  }
  if (data.payload["Company Address"] !== undefined) {
    updatePayload.address1_composite = data.payload["Company Address"];
  }
  if (data.payload["Region"] !== undefined) {
    updatePayload.dogma_region = data.payload["Region"];
  }
  if (data.payload["Customer Type"] !== undefined) {
    updatePayload.customertypecode = data.payload["Customer Type"];
  }
  if (data.payload["Discount % from VS Base Price"] !== undefined) {
    updatePayload.dogma_discountfromvsbaseprice = data.payload["Discount % from VS Base Price"];
  }
  if (data.payload["Credit Terms"] !== undefined) {
    updatePayload.paymenttermscode = data.payload["Credit Terms"];
  }
  if (data.payload["Quote Validity (days)"] !== undefined) {
    updatePayload.dogma_quotevaliditydays = data.payload["Quote Validity (days)"];
  }
  if (data.payload["Assigned RSM"] !== undefined) {
    updatePayload.dogma_assignedrsm = data.payload["Assigned RSM"];
  }
  if (data.payload["Currency"] !== undefined) {
    updatePayload["Dealers Currency"] = data.payload["Currency"];
  }

  return await mutator(NetworkMethodEnum.PATCH, URL.accounts, updatePayload);
};

/**
 * Delete an onboarding account
 */
export const deleteOnboardingAccount = async (id: string): Promise<void> => {
  await mutator(NetworkMethodEnum.DELETE, URL.accounts, { id });
};

/**
 * Get onboarding account list with pagination and filtering
 */
export const getOnboardingAccountList = async (
  queryParams?: ICommonQueryParams
): Promise<ICommonListApiRes<IOnboardingAccount>> => {
  const query = generateQueryParamsForPagination(queryParams);
  return await fetcher(
    `${URL.accounts}${query}&$orderby=createdon desc&$expand=primarycontactid`,
    queryParams?.pageSize,
  );
};

/**
 * Get single onboarding account by ID
 */
export const getOnboardingAccountDetail = async (id: string): Promise<IOnboardingAccount> => {
  const response = await fetcher(`${URL.accounts}?$filter=accountid eq ${id}`);
  return response?.value?.[0] ?? response;
};

/**
 * Get onboarding account by email
 */
export const getOnboardingAccountByEmail = async (email: string): Promise<IOnboardingAccount | null> => {
  try {
    const query = `?$count=true&$filter=emailaddress1 eq '${email}'&$top=1`;
    const response = await fetcher(`${URL.accounts}${query}`);

    if (response.value && response.value.length > 0) {
      return response.value[0];
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Get account count by status code (optional filter)
 */
export const getAccountCountByStatusApi = async (
  statuscode?: number,
  region?: number
): Promise<any> => {
  let url = `${URL.accounts}?$apply=`;

  // Add filter clause if needed
  const filters = [];
  if (statuscode !== undefined) {
    filters.push(`statuscode eq ${statuscode}`);
  }
  if (region !== undefined && region !== null) {
    filters.push(`dogma_region eq ${region}`);
  }

  if (filters.length > 0) {
    url += `filter(${filters.join(' and ')})/`;
  }

  url += `aggregate($count as count)`;
  return await fetcher(url);
};

/**
 * Get counts grouped by all status codes
 */
export const getAllAccountCountsByStatusApi = async (region?: number): Promise<any> => {
  let url = `${URL.accounts}?$apply=`;

  // Add region filter if provided
  if (region !== undefined && region !== null) {
    url += `filter(dogma_region eq ${region})/`;
  }

  url += `groupby((statuscode), aggregate($count as count))`;
  return await fetcher(url);
};

/**
 * Deactivate an onboarding account (statecode=1, statuscode=2)
 */
export const deactivateOnboardingAccount = async (payload: {
  id: string;
  statecode: number;
  statuscode: number;
  dogma_deactivationstate: string;
}): Promise<void> => {
  await MiddlewareAPI.patch(`/AccountApi`, payload);
};

/**
 * Patch a single field on an account
 */
export const patchAccountFieldApi = async (payload: { id: string; [key: string]: any }): Promise<void> => {
  await MiddlewareAPI.patch(`/AccountApi`, payload);
};

/**
 * Save custom discount configuration for an account
 */
export const saveCustomDiscountConfig = async (payload: {
  id: string;
  dogma_customdiscountconfig: string;
}): Promise<void> => {
  await MiddlewareAPI.patch(`/AccountApi`, payload);
};

/**
 * Activate an onboarding account
 */
export const activateOnboardingAccount = async (payload: {
  id: string;
  statecode: number;
  statuscode: number;
  dogma_deactivationstate: null;
}): Promise<void> => {
  await MiddlewareAPI.patch(`/AccountApi`, payload);
};

/**
 * Send onboarding reminder email
 */
export interface ISendReminderEmailPayload {
  accountIds: string[];
  subject: string;
  body: string;
  isHtml: boolean;
}

export const sendOnboardingReminderEmail = async (
  payload: ISendReminderEmailPayload
): Promise<any> => {
  return await mutator(NetworkMethodEnum.POST, URL.email, payload);
};

// ─── Customer Discount Config API ────────────────────────────────────────────

const discountApiUrl = (accountId: string) =>
  `api/data/AccountDiscountApi?code=${APPENV.MIDDLEWARE_FUNCTION_CODE}&accountId=${accountId}&fileName=discount`;

export interface ICustomerDiscountPayload {
  accountId: string;
  discountConfig: object;
  updatedAt: string;
  updatedBy: string;
}

/** GET — fetch existing discount config for this account */
export const getCustomerDiscountConfigApi = async (accountId: string): Promise<ICustomerDiscountPayload | null> => {
  try {
    const response = await API.get(discountApiUrl(accountId));
    return response.data ?? null;
  } catch {
    return null;
  }
};

/** POST — create a new discount config */
export const createCustomerDiscountConfigApi = async (payload: ICustomerDiscountPayload): Promise<any> => {
  return (await API.post(discountApiUrl(payload.accountId), payload)).data;
};

/** POST — update an existing discount config (same method as create) */
export const updateCustomerDiscountConfigApi = async (payload: ICustomerDiscountPayload): Promise<any> => {
  return (await API.post(discountApiUrl(payload.accountId), payload)).data;
};
